'use client';

import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

// 네이버 광고 전환 추적 계정 ID
const WCS_ACCOUNT = 's_1f49510656e';

// view_content(콘텐츠 조회) 전환을 발생시킬 전환경로.
// 이 경로(또는 하위 상세)에 처음 들어온 순간 브라우저 세션당 한 번만 보낸다(VIEW_CONTENT_SENT_KEY).
const VIEW_CONTENT_PATHS = [
  '/about-us', // 드라이빙존은?(+ 브랜드 소개/커리큘럼 등 하위)
  '/pricing', // 요금안내
  '/exam', // 시험안내(+ 하위 안내 페이지)
  '/recommend-class', // 추천 클래스(+ 하위 클래스 상세)
  '/branch', // 지점안내(+ /branch/[지점])
  '/academy', // 학원 찾기(+ /academy/[학원])
  '/event', // 이벤트(+ /event/[이벤트])
  '/story', // 운전이야기(+ 분류 목록/글 상세)
  '/support', // 고객센터(공지사항/FAQ + 하위 글)
];

// 세션당 한 번만 보냈는지 기록하는 sessionStorage 키.
// 페이지마다·쿼리 변경마다 보내면 한 번의 광고 클릭이 전환 여러 건으로 잡힌다(2026-10 클릭 147건에 전환 1,158건).
const VIEW_CONTENT_SENT_KEY = 'naver_wcs_view_content_sent';

// 전환경로 여부 판정: 경로 자체(목록/랜딩) 또는 하위 경로(상세)면 발화
function isViewContentPath(pathname: string): boolean {
  return VIEW_CONTENT_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/** 이 세션에서 view_content 를 처음 보내는 것인지. 저장소를 못 쓰면 페이지 수명 동안만 막는다 */
let viewContentSentInMemory = false;
function claimViewContent(): boolean {
  if (viewContentSentInMemory) return false;
  viewContentSentInMemory = true;
  try {
    if (window.sessionStorage.getItem(VIEW_CONTENT_SENT_KEY)) return false;
    window.sessionStorage.setItem(VIEW_CONTENT_SENT_KEY, '1');
  } catch {
    // 사생활 보호 모드 등 — 메모리 기준으로만 막는다
  }
  return true;
}

declare global {
  interface Window {
    wcs?: {
      /** domain: 유입 쿠키를 둘 최상위 도메인(생략 시 현재 호스트) */
      inflow: (domain?: string) => void;
      trans: (conv: Record<string, string>) => void;
    };
    wcs_do?: () => void;
    wcs_add?: Record<string, string>;
    _nasa?: Record<string, unknown>;
  }
}

/**
 * 네이버 Wcs(웹로그분석/광고 전환 추적).
 *
 * wcslog.js를 로드한 뒤(onLoad로 ready 처리), SPA 라우트 전환마다 wcs_do()를
 * 재호출해 페이지뷰가 누락되지 않게 한다. inflow()는 최초 유입 시점에만 의미가
 * 있으므로 첫 진입에서 한 번만 호출한다.
 *
 * 전환경로(VIEW_CONTENT_PATHS)의 목록/랜딩 또는 하위 상세 페이지에 처음 진입하면 세션당 한 번
 * wcs.trans({ type: 'view_content' })를 호출해 콘텐츠 조회 전환을 기록한다.
 * 상담 신청 완료는 trackNaverLead()로 따로 보낸다.
 *
 * 쿼리스트링만 바뀌는 경우(목록 필터·페이지 이동, ?page=1 자동 보정)는 같은 화면이므로
 * 페이지뷰·전환을 다시 보내지 않는다.
 */
export default function NaverWcs() {
  const pathname = usePathname();
  const [ready, setReady] = useState(false);
  const inflowDone = useRef(false);

  useEffect(() => {
    if (!ready || typeof window === 'undefined' || !window.wcs) return;

    window.wcs_add = window.wcs_add || {};
    window.wcs_add['wa'] = WCS_ACCOUNT;
    window._nasa = window._nasa || {};

    // 최초 진입에서만 유입 출처 기록
    if (!inflowDone.current) {
      window.wcs.inflow();
      inflowDone.current = true;
    }

    window.wcs_do?.();

    // 전환경로 첫 진입 시 콘텐츠 조회(view_content) 전환 기록(세션당 한 번)
    if (isViewContentPath(pathname) && claimViewContent()) {
      window.wcs.trans({ type: 'view_content' });
    }
  }, [ready, pathname]);

  return (
    <Script
      src="//wcs.naver.net/wcslog.js"
      strategy="afterInteractive"
      onLoad={() => setReady(true)}
    />
  );
}

/**
 * 네이버 광고 잠재고객(lead) 전환 — 상담 신청이 접수됐을 때 부른다.
 *
 * 같은 전역(wcs_add)을 네이버 애널리틱스(naver-analytics.tsx)도 쓰므로 보내기 직전에 광고 계정으로
 * 맞춘다. 스크립트가 아직 없거나 실패해도 신청 흐름에 영향을 주지 않는다.
 */
export function trackNaverLead(): void {
  try {
    if (typeof window === 'undefined' || !window.wcs) return;
    window.wcs_add = window.wcs_add || {};
    window.wcs_add['wa'] = WCS_ACCOUNT;
    window.wcs.trans({ type: 'lead' });
  } catch {
    // 광고 전환은 부가 기능 — 실패해도 화면 동작에 영향이 없어야 한다
  }
}
